import { useQuery } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';
import { compareVersions } from '@/lib/versionCheck';

interface VersionCheckResult {
  needsUpdate: boolean;
  loading: boolean;
  currentVersion: string | null;
  minVersion: string | null;
  platform: 'ios' | 'android' | null;
}

/**
 * Checks whether the native app version is below the remotely-configured
 * minimum. Returns `needsUpdate: false` immediately on web.
 *
 * Fails open: if the Supabase fetch fails, assumes the version is fine
 * so the app is never bricked by a config outage.
 */
export function useVersionCheck(): VersionCheckResult {
  const isNative = Capacitor.isNativePlatform();

  const { data, isLoading } = useQuery({
    queryKey: ['version-check'],
    queryFn: async () => {
      const info = await App.getInfo();
      const platform = Capacitor.getPlatform() as 'ios' | 'android';
      const configKey = `min_version_${platform}`;

      const { data: row, error } = await supabase
        .from('app_config')
        .select('value')
        .eq('key', configKey)
        .single();

      // Fail open: if we can't fetch the config, don't block the user
      if (error || !row) {
        return { needsUpdate: false, currentVersion: info.version, minVersion: null, platform };
      }

      const needsUpdate = compareVersions(info.version, row.value) === -1;
      return { needsUpdate, currentVersion: info.version, minVersion: row.value, platform };
    },
    enabled: isNative,
    staleTime: 60 * 60 * 1000, // 1 hour
    retry: false, // fail open — don't retry on error
  });

  if (!isNative) {
    return { needsUpdate: false, loading: false, currentVersion: null, minVersion: null, platform: null };
  }

  return {
    needsUpdate: data?.needsUpdate ?? false,
    loading: isLoading,
    currentVersion: data?.currentVersion ?? null,
    minVersion: data?.minVersion ?? null,
    platform: data?.platform ?? null,
  };
}
